import { SerializedStyles } from "@emotion/react";
import { ReactNode, Ref, memo, useCallback } from "react";
import { usePartialSubmit } from "src/apps/AppFlow/pages/hooks";
import { FormFlowPage } from "src/components";
import DropdownSelector from "src/components/DropdownSelector";
import { useMultiSelectOnChange } from "src/hooks";

import { enums, formatters } from "@fraction/shared";

const { SEX_MAP } = formatters.enums;

export interface ApplicantSexProps {
  onChange: (value: enums.Sex[]) => void;
  value?: enums.Sex[];
  title?: string;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
  children?: ReactNode;
}

const ApplicantSex = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  onChange,
  title,
  children,
  ...props
}: ApplicantSexProps) => {
  const validate = useCallback(() => !!value, [value]);
  const { handleNext, nextDisabled, nextLoading } = usePartialSubmit({ onNext, validate });

  const chosenNonDisclosed = value?.includes(enums.Sex.NOT_DISCLOSED);
  const handleChange = useMultiSelectOnChange<enums.Sex>(onChange);

  return (
    <FormFlowPage
      {...props}
      title={title || "What is your legal sex?"}
      description="Federal regulations require us to ask. We do not discriminate on the basis of this information or whether you choose to provide it."
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
    >
      <DropdownSelector
        label="Legal sex"
        focusRef={focusRef}
        value={value}
        onChange={handleChange}
        isMulti={!chosenNonDisclosed}
        options={Object.values(enums.Sex)
          .filter((type) =>
            (value?.length || 0) > 0 && !chosenNonDisclosed ? type !== enums.Sex.NOT_DISCLOSED : true
          )
          .map((type) => ({
            value: type,
            label: SEX_MAP[type],
          }))}
      />
      {children}
    </FormFlowPage>
  );
};

export default memo(ApplicantSex);
